import { FormControl, FormHelperText, Input, Switch } from '@mui/joy';

import { ReactNode, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { mutate as mutateGlobal } from 'swr';

import { EXTENDED_STATS_RUN_SELECT } from '@/Pages/Settings/utils/constants';
import { validateBackfillDate } from '@/Pages/Settings/utils/helpers';
import { useRecentRunsAndExamples } from '@/Pages/Settings/utils/useRecentRuns';
import { ExpandableErrorAlert } from '@/components/ExpandableErrorAlert';
import { Pane } from '@/components/Pane';
import { RulesCustomEvaluatorForm } from '@/components/Rules/RulesCustomEvaluatorForm';
import { FetcherParams } from '@/data/fetcher';
import { OrgConfigs, useOrgConfig } from '@/hooks/useOrgConfig';
import { useOrganizationId, useRunRulesMutation } from '@/hooks/useSwr';
import { DatasetRuleMutateBody, RuleSchema } from '@/types/schema';
import { appDatasetsPath, appOrganizationPath } from '@/utils/constants';

import {
  BackfillDateInput,
  BackfillFromFormLabel,
} from '../Rules/SessionRulesCrudFormV2';
import { Skeleton } from '../Skeleton';
import { DatasetEvaluatorCrudForm } from './EvaluatorCrudForm';
import { DatasetEvaluatorCrudPaneHeader } from './EvaluatorCrudPaneHeader';
import { EvaluatorCrudPaneLayout } from './EvaluatorCrudPaneLayout';
import { DatasetPreviewPanel } from './PreviewPanel';
import { useEvaluatorStore } from './store/evaluatorStore';
import { DatasetEvaluatorCrudPaneProps } from './types';

export function DatasetEvaluatorCrudPane(props: DatasetEvaluatorCrudPaneProps) {
  const importEvaluator = useEvaluatorStore((state) => state.importEvaluator);
  const setName = useEvaluatorStore((state) => state.setName);
  const setFewShotEnabled = useEvaluatorStore(
    (state) => state.setFewShotEnabled
  );
  const setNumFewShotExamples = useEvaluatorStore(
    (state) => state.setNumFewShotExamples
  );

  const { setOpen, open, onRuleSaved } = props;

  useEffect(() => {
    if (open) {
      importEvaluator(
        props.evaluatorRule?.evaluators?.[0].structured,
        'dataset',
        props.evaluatorRule?.backfill_from,
        props.evaluatorRule?.evaluator_version
      );
      if (props.evaluatorRule) {
        setName(props.evaluatorRule?.display_name ?? '', true);
        setFewShotEnabled(
          props.evaluatorRule?.use_corrections_dataset ?? false
        );
        setNumFewShotExamples(props.evaluatorRule?.num_few_shot_examples ?? 5);
      }
    }
  }, [
    open,
    props.evaluatorRule,
    importEvaluator,
    setName,
    setFewShotEnabled,
    setNumFewShotExamples,
  ]);

  const alignQueueId =
    props.evaluatorRule &&
    'alignment_annotation_queue_id' in props.evaluatorRule
      ? props.evaluatorRule.alignment_annotation_queue_id
      : undefined;

  const isCreating = !props.evaluatorRule?.display_name;

  const navigate = useNavigate();
  const organizationId = useOrganizationId();
  const currentUrl = useLocation();

  return (
    <EvaluatorCrudPaneLayout
      title={props.evaluatorRule?.display_name ?? 'Create Evaluator'}
      open={open}
      setOpen={setOpen}
      left={
        <DatasetEvaluatorCrudForm
          datasetId={props.datasetId}
          fewShotDatasetId={props.evaluatorRule?.corrections_dataset_id}
          evaluatorVersion={props.evaluatorRule?.evaluator_version}
          alignQueueId={alignQueueId}
          isCreating={isCreating}
          preventReset={props.preventReset}
        />
      }
      right={<DatasetPreviewPanel datasetId={props.datasetId} />}
      header={
        <DatasetEvaluatorCrudPaneHeader
          datasetId={props.datasetId}
          selectedRule={props.evaluatorRule}
          onClose={() => {
            setOpen(false);
          }}
          onSuccess={async (ruleId) => {
            setOpen(false);
            await mutateGlobal((key: FetcherParams) =>
              key?.url?.includes('commits')
            );
            onRuleSaved(ruleId);
            if (
              isCreating &&
              currentUrl.pathname.includes(`/${appDatasetsPath}/`)
            ) {
              navigate(
                `/${appOrganizationPath}/${organizationId}/${appDatasetsPath}/${props.datasetId}?tab=2`
              );
            }
          }}
          onDiscard={() => {
            setOpen(false);
          }}
        />
      }
    />
  );
}

function DatasetRulesCrudForm(props: {
  id?: string;
  prefilledData?: Pick<DatasetRuleMutateBody, 'dataset_id'>;
  initialData?: DatasetRuleMutateBody;
  onSuccess: (id: string) => void;
  onCancel: () => void;
}) {
  'use no memo';
  const [applyToPastRuns, setApplyToPastRuns] = useState(false);
  const { value: showExtendedStatsFlag } = useOrgConfig(
    OrgConfigs.enable_include_extended_stats
  );

  const form = useForm<DatasetRuleMutateBody>({
    defaultValues: {
      ...props.initialData,
      is_enabled: props.initialData?.is_enabled ?? true,
      include_extended_stats:
        props.initialData?.include_extended_stats ?? false,
    },
  });
  const mutate = useRunRulesMutation(props.id, {
    onSuccess: (data) => props.onSuccess(data.id),
  });

  const onSubmit = form.handleSubmit((json) => {
    const data = {
      ...json,
      sampling_rate: 1,
      ...props.prefilledData,
    };
    if (!applyToPastRuns && !props.initialData?.backfill_from) {
      delete data.backfill_from;
    }
    mutate.trigger({ json: data });
  });

  const includeExtendedStats = form.watch('include_extended_stats');

  const { recentRuns, runIdsToExample, isLoading, examples } =
    useRecentRunsAndExamples({
      datasetId: props.prefilledData?.dataset_id,
      variant: 'dataset',
      select: includeExtendedStats ? EXTENDED_STATS_RUN_SELECT : undefined,
    });

  const navigate = useNavigate();
  const organizationId = useOrganizationId();
  const currentUrl = useLocation();

  const ruleConfigComponent = (
    <>
      <FormControl error={!!form.formState.errors.display_name}>
        <div className="mb-2 text-sm font-semibold">Evaluator Name</div>
        <Input
          type="text"
          className="rounded-md border border-secondary bg-transparent px-2 py-[7px] outline-none"
          {...form.register('display_name', { required: 'Name is required' })}
        />
        <FormHelperText className="empty:hidden">
          {form.formState.errors.display_name?.message}
        </FormHelperText>
      </FormControl>

      {!props.initialData && (
        <div className="flex flex-row items-center gap-2">
          <div className="text-sm font-semibold">Apply to past runs</div>
          <Switch
            size="sm"
            checked={applyToPastRuns}
            onChange={(event) => setApplyToPastRuns(event.target.checked)}
          />
        </div>
      )}

      {showExtendedStatsFlag && (
        <div className="flex flex-row items-center gap-2">
          <div className="text-sm font-semibold">
            Include extended stats (feedback, costs, tokens)
          </div>
          <Controller
            name="include_extended_stats"
            control={form.control}
            render={({ field }) => (
              <Switch
                size="sm"
                checked={field.value || false}
                onChange={field.onChange}
              />
            )}
          />
        </div>
      )}

      {(applyToPastRuns ||
        (props.initialData && props.initialData.backfill_from)) && (
        <FormControl
          error={!!form.formState.errors.backfill_from}
          sx={{ display: 'flex', flexDirection: 'row', gap: '8px' }}
        >
          <BackfillFromFormLabel disabled={!!props.initialData} />
          <Controller
            name="backfill_from"
            control={form.control}
            rules={{
              validate: validateBackfillDate,
            }}
            render={({ field, fieldState }) => (
              <BackfillDateInput
                value={field.value}
                onChange={field.onChange}
                disabled={!!props.initialData}
                error={fieldState.error}
              />
            )}
          />
        </FormControl>
      )}
    </>
  );

  return (
    <div className="flex h-full flex-col gap-4">
      <ExpandableErrorAlert error={mutate.error} />
      <Controller
        control={form.control}
        name="code_evaluators"
        render={({ field }) =>
          isLoading ? (
            <div className="flex flex-1 gap-4">
              <Skeleton className="h-full flex-1" />
              <Skeleton className="h-full flex-1" />
            </div>
          ) : (
            <RulesCustomEvaluatorForm
              value={field.value?.[0]}
              onChange={(value) => field.onChange([value])}
              variant="dataset"
              onSave={(value) => {
                field.onChange([value]);
                onSubmit();
                if (
                  !props.initialData &&
                  currentUrl.pathname.includes(`/${appDatasetsPath}/`) &&
                  props.prefilledData?.dataset_id
                ) {
                  navigate(
                    `/${appOrganizationPath}/${organizationId}/${appDatasetsPath}/${props.prefilledData?.dataset_id}?tab=2`
                  );
                }
              }}
              saveMutating={mutate.isMutating}
              sampleRun={recentRuns.data?.runs?.[0]}
              sampleExample={
                recentRuns.data?.runs?.[0]
                  ? runIdsToExample[recentRuns.data?.runs?.[0].id]
                  : examples.data?.[0]
              }
              configureRuleComponent={ruleConfigComponent}
              includeExtendedStats={includeExtendedStats}
            />
          )
        }
      />
    </div>
  );
}
function isRuleSchema(
  rule: RuleSchema | DatasetRuleMutateBody
): rule is RuleSchema {
  return 'id' in rule && rule.id !== undefined;
}

export function DatasetCodeEvaluatorCrudPane(props: {
  datasetId?: string;
  rule?: DatasetRuleMutateBody | RuleSchema;
  title?: string;
  open: boolean;
  topBarRightElement?: ReactNode;
  onClose: () => void;
  onSuccess?: (id: string) => void;
}) {
  const evaluatorText = 'Custom Code Evaluator';
  const id = props.rule && isRuleSchema(props.rule) ? props.rule.id : undefined;

  return (
    <Pane
      open={props.open}
      onClose={props.onClose}
      dialogStyle={{
        marginLeft: '80px',
      }}
      className="pt-0"
      title={
        props.title ?? (
          <div className="flex flex-col gap-0.5">
            <h3 className="text-xl font-medium">
              {props.rule ? `Edit ${evaluatorText}` : `Add ${evaluatorText}`}
            </h3>
            <p className="text-sm font-normal text-tertiary">
              {props.rule
                ? `Edit the existing ${evaluatorText}`
                : `Add a new ${evaluatorText} for the current dataset`}
            </p>
          </div>
        )
      }
      topBarRightElement={props.topBarRightElement}
    >
      <DatasetRulesCrudForm
        prefilledData={
          props.datasetId ? { dataset_id: props.datasetId } : undefined
        }
        id={id}
        initialData={props.rule as DatasetRuleMutateBody | undefined}
        onSuccess={(id) => {
          props.onSuccess?.(id);
          props.onClose();
        }}
        onCancel={props.onClose}
      />
    </Pane>
  );
}
